/**
 * Account & Profile Articles — Russian
 */

import type { HelpArticle } from "../../../types";

export const ACCOUNT_ARTICLES_RU: HelpArticle[] = [
  {
    id: "acc-001",
    slug: "managing-your-profile",
    title: "Управление профилем",
    description: "Как обновить личную информацию и настройки",
    category: "account",
    order: 1,
    keywords: ["профиль", "настройки", "личные", "данные", "обновить"],
    content: `
# Управление профилем

Поддерживайте профиль в актуальном состоянии для корректной работы и соответствия требованиям.

## Доступ к настройкам профиля

1. Нажмите на аватар в боковом меню
2. Выберите «Настройки»
3. Перейдите на вкладку «Профиль»

## Что можно изменить

**Личные данные**
- Отображаемое имя
- Номер телефона (опционально)
- Предпочитаемый язык

**Адрес**
- Улица
- Город
- Область/регион
- Почтовый индекс
- Страна

## Что нельзя изменить

Некоторые данные блокируются после верификации:
- Полное имя (обратитесь в поддержку)
- Дата рождения
- Гражданство
- Данные документа

## Обновление адреса

Если вы переехали:
1. Откройте Настройки > Профиль
2. Нажмите «Обновить адрес»
3. Введите новые данные
4. При переезде в другую страну может потребоваться повторная верификация

## Фото профиля

- Опционально, но рекомендуется
- Помогает идентифицировать аккаунт
- Должно быть подходящим (без логотипов и т.д.)

## Настройки уведомлений

Управляйте уведомлениями:
- Обновления транзакций
- Оповещения безопасности
- Маркетинговые сообщения
- Анонсы продукта

## Экспорт данных

Вы можете скачать свои данные:
1. Откройте Настройки > Конфиденциальность
2. Нажмите «Экспорт данных»
3. Ссылка придёт на email
4. Данные в формате JSON
    `.trim(),
  },
  {
    id: "acc-002",
    slug: "changing-email-phone",
    title: "Смена email или телефона",
    description: "Как обновить контактную информацию",
    category: "account",
    order: 2,
    keywords: ["email", "телефон", "изменить", "обновить", "контакты"],
    content: `
# Смена email или телефона

Email — основной способ входа. Вот как безопасно его изменить.

## Смена email

### Требования
- Доступ к текущему email
- Доступ к новому email
- 2FA, если включена

### Шаги
1. Откройте Настройки > Безопасность
2. Нажмите «Сменить email»
3. Введите новый email
4. Подтвердите владение текущим email
5. Подтвердите владение новым email
6. Подтверждение отправляется на оба адреса

### Важно
- Оба email должны быть подтверждены
- Процесс занимает несколько минут
- Старый email нельзя использовать сразу

## Добавление телефона

Телефон — опционально, но добавляет безопасности:
1. Откройте Настройки > Профиль
2. Нажмите «Добавить телефон»
3. Введите номер с кодом страны
4. Подтвердите кодом из SMS

## Смена телефона

1. Откройте Настройки > Профиль
2. Нажмите «Сменить телефон»
3. Подтвердите текущий номер
4. Введите и подтвердите новый

## Потеряли доступ к email?

Если не можете войти в почту:
1. Сразу напишите в поддержку
2. Пройдите верификацию личности
3. Ответьте на контрольные вопросы
4. Восстановление занимает 24-48 часов

## Безопасность

- Смена email вызывает оповещения
- Проверяется история входов
- Крупные выводы могут быть приостановлены
- Может потребоваться дополнительная верификация
    `.trim(),
  },
  {
    id: "acc-003",
    slug: "account-verification-tiers",
    title: "Уровни верификации",
    description: "Понимание разных уровней верификации",
    category: "account",
    order: 3,
    keywords: ["верификация", "уровень", "лимиты", "апгрейд"],
    content: `
# Уровни верификации

Уровень верификации определяет ваши лимиты и доступные функции.

## Уровни

### Уровень 1: Базовый
**Требования:**
- Только подтверждение email

**Лимиты:**
- Только просмотр
- Транзакции недоступны

### Уровень 2: Стандартный
**Требования:**
- Документ госообразца
- Селфи-верификация

**Лимиты:**
- $50,000 в месяц
- $10,000 за транзакцию
- Все стандартные функции

### Уровень 3: Расширенный
**Требования:**
- Стандартный + подтверждение адреса
- Дополнительные документы

**Лимиты:**
- $500,000 в месяц
- $100,000 за транзакцию
- Приоритетная поддержка

### Уровень 4: Корпоративный
**Требования:**
- Бизнес-верификация
- Персональный менеджер

**Лимиты:**
- Индивидуальные лимиты
- API-доступ
- VIP-поддержка

## Как повысить уровень

### Стандартный → Расширенный
1. Откройте Настройки > Верификация
2. Нажмите «Повысить до Расширенного»
3. Загрузите подтверждение адреса:
   - Счёт за коммунальные услуги (< 3 месяцев)
   - Банковская выписка (< 3 месяцев)
   - Налоговый документ
4. Ожидайте проверки (24-48 часов)

### Расширенный → Корпоративный
Свяжитесь с отделом продаж: enterprise@supabank.xyz

## Проверка уровня

Посмотрите текущий уровень:
1. Откройте Настройки > Верификация
2. Смотрите бейдж «Текущий уровень»
3. Проверьте оставшиеся лимиты

## Сравнение преимуществ

| Функция | Базовый | Стандарт | Расширенный | Корпоратив |
|---------|---------|----------|-------------|------------|
| Депозиты | | | | |
| Выводы | | | | |
| API | | | | |
| Приоритетная поддержка | | | | |
| Персональный менеджер | | | | |
    `.trim(),
  },
  {
    id: "acc-004",
    slug: "closing-your-account",
    title: "Закрытие аккаунта",
    description: "Как закрыть аккаунт Supabank",
    category: "account",
    order: 4,
    keywords: ["закрыть", "удалить", "аккаунт", "отмена"],
    content: `
# Закрытие аккаунта

Если нужно закрыть аккаунт — вот что важно знать.

## Перед закрытием

Убедитесь:
- Все транзакции завершены
- Средства выведены
- Нет открытых споров
- Скачаны нужные записи

## Процесс закрытия

1. Откройте Настройки > Аккаунт
2. Прокрутите до «Закрыть аккаунт»
3. Нажмите «Запросить закрытие»
4. Подтвердите личность
5. Выберите причину закрытия
6. Подтвердите

## Что произойдёт дальше

**Сразу:**
- Доступ к аккаунту закрыт
- Новые транзакции невозможны

**В течение 30 дней:**
- Проверка хранения данных
- Обработка остатков
- Финальные выписки

**Навсегда:**
- Аккаунт удалён полностью
- Восстановление невозможно
- Email нельзя использовать 90 дней

## Хранение данных

Мы храним некоторые данные по закону:
- Записи транзакций (7 лет)
- Документы идентификации (5 лет)
- Записи комплаенса (варьируется)

## Вывод средств

Перед закрытием:
1. Проверьте все балансы
2. Запросите вывод в банк
3. Дождитесь завершения
4. Затем запросите закрытие

## Восстановление

После закрытия аккаунт восстановить нельзя. Нужно создать новый и пройти верификацию заново.

## Вопросы?

Если закрываете из-за проблемы — сначала напишите в поддержку. Возможно, мы сможем помочь.
    `.trim(),
  },
  {
    id: "acc-005",
    slug: "account-recovery",
    title: "Восстановление аккаунта",
    description: "Как вернуть доступ к аккаунту",
    category: "account",
    order: 5,
    keywords: ["восстановление", "доступ", "заблокирован", "помощь"],
    content: `
# Восстановление аккаунта

Потеряли доступ к аккаунту? Вот как его восстановить.

## Частые проблемы доступа

### Magic Link не приходит
- Проверьте спам
- Проверьте правильность email
- Попробуйте другой почтовый клиент
- Подождите пару минут и повторите

### Magic Link истёк
- Ссылки действуют 15 минут
- Запросите новую ссылку
- Проверьте, нет ли нескольких писем

### Аккаунт заблокирован
- Слишком много неудачных попыток
- Подождите 30 минут
- Или обратитесь в поддержку

## Шаги восстановления

### С доступом к email

1. Откройте страницу входа
2. Введите email
3. Нажмите «Отправить Magic Link»
4. Проверьте почту (и спам)
5. Перейдите по ссылке за 15 минут

### Без доступа к email

1. Напишите на support@supabank.xyz
2. Укажите:
   - Email аккаунта
   - Полное имя
   - Дату рождения
   - Детали последней транзакции
3. Пройдите верификацию личности
4. Поддержка поможет восстановить доступ

## Верификация для восстановления

Может потребоваться:
- Фото документа
- Селфи с рукописной запиской
- Подтверждение адреса
- Информация об аккаунте

## Сроки

| Тип восстановления | Время |
|-------------------|-------|
| Стандартный вход | Сразу |
| Сброс пароля/2FA | 1-2 часа |
| Полное восстановление | 24-48 часов |

## Профилактика

- Защищайте свой email
- Включите 2FA
- Сохраните резервные коды
- Обновляйте контактные данные
- Используйте менеджер паролей для почты
    `.trim(),
  },
  {
    id: "acc-006",
    slug: "business-accounts",
    title: "Бизнес-аккаунты",
    description: "Информация о бизнес-аккаунтах",
    category: "account",
    order: 6,
    keywords: ["бизнес", "корпоративный", "компания", "организация"],
    content: `
# Бизнес-аккаунты

Supabank предлагает бизнес-аккаунты для компаний, которым нужно конвертировать крипту в фиат в масштабе.

## Преимущества бизнес-аккаунта

- Повышенные лимиты
- Доступ для нескольких пользователей
- API-интеграция
- Выделенная поддержка
- Кастомная отчётность
- Выставление счетов

## Кто может получить

Бизнес-аккаунты доступны для:
- Зарегистрированных корпораций
- ООО и партнёрств
- ИП (с бизнес-лицензией)
- Некоммерческих организаций

## Необходимые документы

### Документы компании
- Свидетельство о регистрации
- Устав
- Бизнес-лицензия (если есть)
- Налоговое свидетельство

### Верификация владельцев
- Информация о бенефициарах
- Реестр акционеров
- Идентификация директоров

### Подтверждение адреса
- Счёт за коммунальные услуги компании
- Банковская выписка
- Договор аренды

## Процесс подачи

1. **Регистрация** как физлицо
2. **Пройдите личную KYC**
3. **Запросите бизнес-апгрейд** в настройках
4. **Отправьте бизнес-документы**
5. **Проверка** (3-5 рабочих дней)
6. **Активация аккаунта**

## Доступ для нескольких пользователей

Бизнес-аккаунты могут иметь:
- Несколько администраторов
- Пользователей с правами просмотра
- Рабочие процессы одобрения
- Журнал активности

## Лимиты для бизнеса

| Уровень | Месячный объём | За транзакцию |
|---------|----------------|---------------|
| Business Standard | $1,000,000 | $250,000 |
| Business Pro | $5,000,000 | $500,000 |
| Enterprise | Индивидуально | Индивидуально |

## API-доступ

Бизнес-аккаунты включают:
- REST API
- Webhook-уведомления
- Sandbox-окружение
- Техническую документацию
- Поддержку интеграции

## Как начать

Напишите на enterprise@supabank.xyz или подайте заявку через настройки аккаунта.
    `.trim(),
  },
];
